import emitter from "./wsEmitter";
import { wsUrl } from "../config/wsConfig";
import { store } from "../../store/store";
import { setSocketStatus } from "../../store/slices/app.slice";
import { buildAuthMessage } from "../utils/buildAuthMessage";

// WebSocket клиент на замыкании
const createWSClient = () => {
	let socket: WebSocket | null = null;
	let reconnectDelay = 3000;
	let shouldReconnect = false;
	let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

	// Снятие отложенного переподключения (если оно было запланировано)
	const clearReconnectTimer = () => {
		if (reconnectTimer !== null) {
			clearTimeout(reconnectTimer);
			reconnectTimer = null;
		}
	};

	// Подключение к WebSocket серверу
	const connect = () => {
		// Если сокет уже открыт или подключается — ничего не трогаем,
		// в т.ч. не сбрасываем статус (иначе индикатор залипает на "connecting").
		if (
			socket &&
			(socket.readyState === WebSocket.OPEN ||
				socket.readyState === WebSocket.CONNECTING)
		) {
			return;
		}

		// Снимаем протухший таймер переподключения, если он остался
		clearReconnectTimer();
		store.dispatch(setSocketStatus("connecting"));

		shouldReconnect = true;
		socket = new WebSocket(wsUrl);

		// Соединение установлено
		socket.onopen = () => {
			// Соединение поднялось — отложенные переподключения больше не нужны
			clearReconnectTimer();
			try {
				sendMessage(buildAuthMessage());
			} catch (e) {
				store.dispatch(setSocketStatus("auth-error"));
			}
		};

		// Получение сообщений от сервера
		socket.onmessage = (event: MessageEvent) => {
			try {
				const parsed = JSON.parse(event.data);
				const { type, ...data } = parsed;

				if (typeof type === "string") {
					emitter.emit(type, data);
				}
			} catch (e) {
				console.error("Failed to parse WS message", e, event.data);
			}
		};

		// Закрытие соединения
		socket.onclose = (ev: CloseEvent) => {
			console.warn("WS closed", ev.code, ev.reason);
			socket = null;

			// если не отключили вручную — переподключаемся
			if (shouldReconnect) {
				store.dispatch(setSocketStatus("offline-reconnect"));
				// Один активный таймер: гасим предыдущий перед планированием нового
				clearReconnectTimer();
				reconnectTimer = setTimeout(connect, reconnectDelay);
			}
		};

		// Ошибка соединения
		socket.onerror = (ev: Event) => {
			console.error("WS error", ev);
			store.dispatch(setSocketStatus("connection-error"));
		};
	};

	// Отключение WebSocket
	const disconnect = () => {
		shouldReconnect = false;
		clearReconnectTimer();

		if (socket) {
			socket.close();
			socket = null;
		}
	};

	// Отправка сообщения на сервер
	const send = (type: string, payload: any) => {
		if (!socket || socket.readyState !== WebSocket.OPEN) {
			console.warn("WS is not open, cannot send", type);
			return;
		}

		socket.send(JSON.stringify({ type, payload }));
	};

	// Отправка уже сформированного объекта сообщения (без обёртки)
	const sendMessage = (message: any) => {
		if (!socket || socket.readyState !== WebSocket.OPEN) {
			console.warn("WS is not open, cannot send message", message);
			return;
		}

		try {
			socket.send(JSON.stringify(message));
		} catch (e) {
			console.error("WS sendMessage failed", e, message);
		}
	};

	// публичное API
	return {
		connect,
		disconnect,
		send,
		sendMessage,
		isOpen: () => socket?.readyState === WebSocket.OPEN,
	};
};

// singleton экземпляр
const wsClient = createWSClient();
export default wsClient;
